/**
 * What a counter sells a vehicle for (owner, 16 Sep 2026: "the prices of outlet
 * are fixed add a option in admin to set the prices of the product wise both
 * for outlet and mitra").
 *
 * The price is the office's, not the counter's: it is set once per product per
 * channel in Administration, and the sale form shows it as a fact rather than
 * asking for it. That is what lets the sale form stop asking a shopkeeper to
 * type a five-figure sum correctly while a customer waits.
 *
 * KEYING IT IS THE WHOLE PROBLEM. A vehicle's model and variant come off the
 * sheet as free text and are spelt every way: CITY CAB E-MAX, CITYCAB EV,
 * CITYCAB; ECO Li, ECO LITHIUM, ECO LI. A price keyed on the raw pair would
 * miss more often than it hit, and a miss means a counter blocked at the
 * counter. So the key is squashed — case, spaces and punctuation dropped, and
 * the handful of real synonyms folded — and everything else is left alone,
 * because ECO, ECO Li and ECO POWER are genuinely different machines at
 * genuinely different prices and must never be folded together.
 */
import { ORG_IDS } from './domain.js';
import { batteryAhFrom, batteryRung } from './sale.js';

export const PRICE_CHANNELS = ['OUTLET', 'MITRA'] as const;
export type PriceChannel = (typeof PRICE_CHANNELS)[number];

/**
 * What a counter of this kind is charged. Anything else has no fixed price.
 *
 * DISTRIBUTOR is the company's OWN store — the party a book keeps for itself,
 * and the counter Vivek sells from. It is charged as an outlet, because that is
 * what it is: the company's own shop selling retail to a walk-in customer, the
 * same as every Bright on Wheels showroom.
 *
 * It was missing until 16 Sep 2026 and returned null, which meant no channel,
 * which meant no price, which meant the sale form refused the sale and told him
 * to ask the office for a price the office had no way to set. Removing the
 * amount box turned that gap from cosmetic into a store that cannot sell.
 *
 * DEALER stays unpriced on purpose: a dealer buys from the maker at a wholesale
 * figure that is not one of these two counter prices.
 */
export function channelForPartyType(type: string | null | undefined): PriceChannel | null {
  if (type === 'COMPANY_OUTLET' || type === 'DISTRIBUTOR') return 'OUTLET';
  if (type === 'SUB_DEALER') return 'MITRA';
  return null;
}

/**
 * A sale out of the store's own hands is at the amount agreed, typed on the
 * form — not at a list price (owner, 18 Sep 2026: "vivek sales' sale form
 * will be different — he will fill the amount also"). Decided by WHO SELLS —
 * the counter the vehicle stands with — so the store's own sign-in and the
 * office selling a vehicle standing in the store see the same form (22 Oct
 * 2026: Surbhi's sale of a store vehicle was refused "Enter the sale amount"
 * from a form that never asked for one). The server and the form both read
 * this.
 */
export function sellsAtTypedAmount(sellerPartyType: string | null | undefined): boolean {
  return sellerPartyType === 'DISTRIBUTOR';
}

export function priceChannelLabel(channel: PriceChannel): string {
  return channel === 'OUTLET' ? 'Outlet' : 'Mitr';
}

/**
 * ONE price list, kept in the maker's book (owner, 17 Sep 2026: "pricing will
 * be only in autozone side they only are the manufacturer no matter who sells
 * the vehicle keep pricing to autozone only").
 *
 * Bright Autozone builds every vehicle the network sells, so it is the one
 * party with a price for each of them. A counter in Bright on Wheels, FAB EV,
 * TVS or the practice book reads this list; the office sets it in Autozone's
 * book and nowhere else. A per-book list — what shipped on 16 Sep — meant the
 * same machine priced four times, and three of the four left blank.
 */
export const PRICE_BOOK = ORG_IDS.wholesale;

/**
 * Which figures a counter of this kind may be sold at, in order.
 *
 * "The prices are only for mitra right now" (owner, 17 Sep 2026): a single
 * figure per product, entered under Mitr. An outlet is sold at its own figure
 * where the office has set one, and at the mitr figure where it has not — so
 * an outlet is never blocked on a column the office has not started filling.
 */
export function priceChannelsToTry(channel: PriceChannel): readonly PriceChannel[] {
  return channel === 'OUTLET' ? ['OUTLET', 'MITRA'] : ['MITRA'];
}

/**
 * The only spellings that mean the same machine. Applied after squashing, so
 * "ECO LITHIUM", "ECO Li" and "eco-li" all arrive here as one word.
 *
 * Deliberately short. Every entry added here merges two products into one
 * price, so it needs to be a spelling difference and not a specification one.
 */
const SYNONYMS: ReadonlyArray<readonly [RegExp, string]> = [
  [/LITHIUM/g, 'LI'],
  [/LION/g, 'LI'],
];

function squash(text: string | null | undefined): string {
  /*
   * A "+" is part of the name (owner, 17 Sep 2026: "2 new variants are the e
   * max eco + and e max dlx +"). Stripped with the other punctuation it folded
   * DLX+ Li into DLX Li — two products, one price row.
   */
  let out = (text ?? '').toUpperCase().replace(/\+/g, 'PLUS').replace(/[^A-Z0-9]+/g, '');
  for (const [pattern, to] of SYNONYMS) out = out.replace(pattern, to);
  return out;
}

/**
 * The stable identity of a product, from whatever the sheet called it.
 * Empty when the vehicle has no model at all — such a row cannot be priced and
 * the caller must say so rather than pricing it as "".
 */
export function productKey(model: string | null | undefined, variant: string | null | undefined): string {
  const m = squash(model);
  if (!m) return '';
  return `${m}|${squash(variant)}`;
}

/** "1650W" → 1650, "1250" → 1250; nothing readable → null. */
export function motorWattsFrom(text: unknown): number | null {
  const match = /(\d{3,4})\s*W?\b/i.exec(String(text ?? ''));
  return match ? Number(match[1]) : null;
}

/** The CITY CAB PRO, however the sheet spaced it ("CITY CAB PRO", "CITYCAB PRO"). */
export function isCityCabPro(model: string | null | undefined): boolean {
  return squash(model) === 'CITYCABPRO';
}

/**
 * The CITY CAB PRO comes in two trims and neither has a "+" (owner, 24 Sep
 * 2026: "There is no deluxe plus and eco plus in city cab pro only city cab
 * pro eco li and city cab pro dlx li"). The "+" is the E-Max's 1650W motor
 * (17 and 21 Sep); on a Pro it is a slip on the form, so "DLX+ Li" reads
 * "DLX Li" and "ECO+ Li" reads "ECO Li". Every other model is left as given.
 */
export function variantForModel(model: string | null | undefined, variant: string | null | undefined): string | null {
  if (!variant || !isCityCabPro(model)) return variant ?? null;
  return variant.replace(/^(\s*)(DLX|ECO)\s*\+/i, '$1$2');
}

/**
 * The variant the motor says it is.
 *
 * Owner, 21 Sep 2026: "difference between delux and delux plus is 1650w motor
 * comes in deluxe+ and 1250w motor in delux, so the prices are coming out
 * wrong". On the CITY CAB E-MAX the "+" is the motor and nothing else: the
 * sheets spelt 19 vehicles "DLX Li" that carry the 1650W motor, and each was
 * being priced 5,000 short. So the motor decides — a 1650W makes a "+", a
 * 1250W takes it off — for DLX and ECO alike, since ECO+ is the same step
 * with the same motor. Other models keep the variant they were given; a
 * vehicle whose motor is not recorded keeps it too.
 */
export function variantByMotor(
  model: string | null | undefined,
  variant: string | null | undefined,
  motor: unknown,
): string | null {
  // A Pro has no "+" whatever the motor (24 Sep 2026) — see variantForModel.
  if (isCityCabPro(model)) return variantForModel(model, variant);
  if (!variant || squash(model) !== 'CITYCABEMAX') return variant ?? null;
  const watts = motorWattsFrom(motor);
  if (watts === null) return variant;
  const match = /^(\s*)(DLX|ECO)(\+?)(.*)$/i.exec(variant);
  if (!match) return variant;
  const [, lead, family, plus, rest] = match;
  if (watts >= 1500 && !plus) return `${lead}${family}+${rest}`;
  if (watts <= 1300 && plus) return `${lead}${family}${rest}`;
  return variant;
}

/**
 * The variant the battery says it is, on a CITY CAB PRO.
 *
 * Owner, 12 Oct 2026, on a Cargo Jumbo recorded "DLX Li" and priced at the
 * DLX: "it should be eco Li because it has 105ah battery". On the Pro the trim
 * IS the battery — the office's own Prices say so: ECO Li's standard battery
 * is 105 Ah and DLX Li's is 140 Ah, and ECO Li with the 140 Ah step (+15,000)
 * comes to exactly the DLX Li's price. So a Pro sent with a battery of the
 * 105 class (105, 107 — `batteryRung`) is an ECO Li, whatever the form said.
 * Nothing else is changed: a bigger battery on an ECO Li is the battery
 * ladder's upgrade, and no battery recorded keeps the variant as given.
 */
export function variantByBattery(
  model: string | null | undefined,
  variant: string | null | undefined,
  battery: unknown,
): string | null {
  if (!variant || !isCityCabPro(model)) return variant ?? null;
  const ah = batteryAhFrom(typeof battery === 'string' ? battery : battery == null ? null : String(battery));
  if (ah === null || batteryRung(ah) !== 105) return variant;
  // "DLX Li", "DLX LI", "DLX LITHIUM" — the lithium DLX, however the sheet spelt it.
  return variant.replace(/^(\s*)DLX(?=\s*LI)/i, '$1ECO');
}

/**
 * The variant a vehicle was built as: what the form or the sheet called it,
 * corrected by what the bay recorded — the motor decides the "+" on an E-MAX
 * (21 Sep 2026) and the battery decides ECO or DLX on a PRO (12 Oct 2026).
 * The one place both rules are applied, for the dispatch bay and the price.
 */
export function builtVariant(
  model: string | null | undefined,
  variant: string | null | undefined,
  extra: { motor?: unknown; battery_ah?: unknown } | null | undefined,
): string | null {
  return variantByBattery(model, variantByMotor(model, variant, extra?.motor), extra?.battery_ah);
}

/** "City Cab E-Max · ECO Li" — how the product reads on a screen. */
export function productLabel(model: string | null | undefined, variant: string | null | undefined): string {
  const m = (model ?? '').trim();
  const v = (variant ?? '').trim();
  if (!m) return v || 'Unnamed product';
  return v ? `${m} · ${v}` : m;
}

/** Two rows describe the same product. */
export function samePricedProduct(
  a: { model?: string | null; variant?: string | null },
  b: { model?: string | null; variant?: string | null },
): boolean {
  const left = productKey(a.model, a.variant);
  return left !== '' && left === productKey(b.model, b.variant);
}
